import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, ArrowLeft, CheckCircle, Send, ShieldCheck } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { Button, Input, Card, CardContent } from '@/components/ui';

const forgotPasswordSchema = z.object({
  email: z.string().email('有効なメールアドレスを入力してください'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export function ForgotPasswordPage() {
  const { resetPassword, isLoading, error, clearError } = useAuthStore();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      clearError();
      await resetPassword(data.email);
      setSubmittedEmail(data.email);
      setIsSubmitted(true);
    } catch {
      // Error is handled in authStore
    }
  };

  if (isSubmitted) {
    return (
      <div className="animate-fade-in">
        <Card className="overflow-hidden border-[#E8DDCF] shadow-[0_18px_60px_rgba(76,53,32,0.12)]">
          <div className="h-1.5 bg-gradient-to-r from-[#C9955E] via-[#B87333] to-[#D3A577]" />
          <CardContent className="space-y-6 px-6 py-7">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#ECF8F0]">
                <CheckCircle className="h-8 w-8 text-[#22A568]" />
              </div>
              <h2 className="text-2xl font-heading-luxury text-[#1A1A1A]">
                メールを送信しました
              </h2>
              <p className="mt-3 rounded-2xl border border-[#E8E0D4] bg-[#FCF8F2] px-4 py-3 text-sm leading-relaxed text-[#6E6659]">
                <span className="font-medium text-[#1A1A1A]">{submittedEmail}</span>
                <br />
                宛に再設定メールを送信しました。メール内リンクからパスワードを更新してください。
              </p>
              <p className="mt-3 text-xs text-[#8A8174]">
                届かない場合は迷惑メールフォルダもご確認ください。
              </p>
            </div>

            <div className="space-y-3">
              <Button fullWidth onClick={() => setIsSubmitted(false)}>
                別のメールアドレスで再送する
              </Button>
              <Link
                to="/login"
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-[#DED8CF] px-5 py-3 text-sm font-medium text-[#5E564A] transition hover:bg-[#F8F4EE]"
              >
                <ArrowLeft className="h-4 w-4" />
                ログインに戻る
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-5">
      <Link
        to="/login"
        className="inline-flex items-center gap-2 text-sm text-[#756C5F] transition hover:text-copper"
      >
        <ArrowLeft className="h-4 w-4" />
        <span>ログインに戻る</span>
      </Link>

      <Card className="overflow-hidden border-[#E7DCCB] shadow-[0_20px_70px_rgba(76,53,32,0.12)]">
        <div className="h-1.5 bg-gradient-to-r from-[#C9955E] via-[#B87333] to-[#D3A577]" />
        <CardContent className="space-y-6 px-6 py-7">
          <div className="space-y-4 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#F4E7D6]">
              <ShieldCheck className="h-8 w-8 text-copper" />
            </div>
            <div>
              <h2 className="text-2xl font-heading-luxury text-[#1A1A1A]">
                パスワード再設定
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-[#71685B]">
                登録済みメールアドレス宛に、再設定リンクをお送りします。
              </p>
            </div>
            <div className="rounded-2xl border border-[#E9DFD1] bg-[#FCF8F2] px-4 py-3 text-left text-xs leading-relaxed text-[#756C5F]">
              <p className="mb-1.5 inline-flex items-center gap-2 font-medium text-[#4E453B]">
                <Send className="h-3.5 w-3.5 text-copper" />
                送信されるメールについて
              </p>
              <p>メール内リンク先で新しいパスワードを設定できます。</p>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <Input
              {...register('email')}
              type="email"
              label="メールアドレス"
              placeholder="example@email.com"
              error={errors.email?.message}
              leftIcon={<Mail className="h-5 w-5" />}
              autoComplete="email"
            />

            {error && (
              <p className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </p>
            )}

            <Button type="submit" fullWidth isLoading={isLoading}>
              リセットメールを送信
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
